import { CopyCheck } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import type { DuplicateCandidate } from '@/services/api/manual-graph'
import { humanizeIdentifier } from './entity-form'

interface DuplicateCandidatesProps {
  candidates: DuplicateCandidate[]
  /** Show an existing entity instead of creating a new one; absent = nothing to show it in. */
  onShow?: (candidate: DuplicateCandidate) => void
}

/**
 * Entities of the same type that may already be the one being created. Advice,
 * never a gate: creating anyway is allowed, and entity resolution merges two
 * entities that share a unique value.
 */
export function DuplicateCandidates({ candidates, onShow }: DuplicateCandidatesProps) {
  const { t } = useTranslation()
  if (candidates.length === 0) return null
  return (
    <section
      aria-live="polite"
      className="flex min-w-0 flex-col gap-2 rounded-lg border border-warning-border bg-warning-surface p-3"
    >
      <h3 className="flex items-center gap-2 text-sm font-medium text-warning-foreground">
        <CopyCheck className="size-4 shrink-0" aria-hidden />
        {t('manualGraph.duplicates.title')}
      </h3>
      <ul className="flex min-w-0 flex-col gap-1">
        {candidates.map(candidate => (
          <li key={candidate.id} className="flex min-w-0 items-center gap-3">
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm text-foreground" title={candidate.name}>
                {candidate.name}
              </span>
              <span className="block truncate text-xs text-muted-foreground">
                {candidate.reason === 'unique'
                  ? t('manualGraph.duplicates.sameValue', {
                      properties: candidate.matched_properties.map(humanizeIdentifier).join(', '),
                    })
                  : t('manualGraph.duplicates.similarName')}
              </span>
            </span>
            {onShow && (
              <Button variant="ghost" size="xs" onClick={() => onShow(candidate)}>
                {t('manualGraph.duplicates.show')}
              </Button>
            )}
          </li>
        ))}
      </ul>
      {candidates.some(c => c.reason === 'unique') && (
        <p className="text-xs text-muted-foreground">{t('manualGraph.duplicates.mergeHint')}</p>
      )}
    </section>
  )
}
